import { ClearBehaviour, Select } from '@infotech-soe/erp-ui-kit';
import React, { forwardRef, Ref, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { AvailableApplication } from '../../../services/auth/types';

import { loginFormMessages } from './messages';

interface Props {
  applications: AvailableApplication[];
  current: AvailableApplication['name'];
  onChange: (current: AvailableApplication['name']) => void;
  className?: string;
}

const ApplicationSelect = (
  { applications, className, current, onChange }: Props,
  ref: Ref<HTMLInputElement>
): React.JSX.Element => {
  const { formatMessage } = useIntl();

  const staleCurrent =
    current === null || !applications.some(app => app.name === current);

  const currentApplication = useMemo((): string | null => {
    if (applications.length === 0) return null;
    if (!staleCurrent) return current;
    const stored = localStorage.getItem('app');
    return applications.some(app => app.name === stored)
      ? stored
      : applications[0].name;
  }, [current, applications, staleCurrent]);

  const handleChange = useCallback(
    (value: string | null) => onChange(value ?? ''),
    [onChange]
  );

  return (
    <Select
      className={className}
      options={applications.map(({ name, displayName }) => ({
        type: 'option',
        label: displayName,
        value: name,
      }))}
      placeholder={
        applications.length === 0
          ? formatMessage(loginFormMessages.noAvailableApps)
          : undefined
      }
      disabled={applications.length === 0}
      ref={ref}
      value={currentApplication}
      withClear={ClearBehaviour.NONE}
      onChange={handleChange}
    />
  );
};

export default forwardRef(ApplicationSelect);
